<template>
  <div class="page-container financial-transactions-page">
    <portal to="page-name">Movimentações</portal>
    <div class="card card-page">
      <div class="card-header">
        <button class="btn btn-primary btn-icon-left float-right" @click="open">
          <fa-icon :icon="['fal', 'plus']"></fa-icon>
          Novo lançamento
        </button>
        <button class="btn btn-neutral btn-icon-left float-right mr-2"
                :class="{loading: printing}" @click="print"
                :disabled="data.items.length === 0 || printing">
          <fa-icon :icon="['fal', 'print']"></fa-icon>
          Imprimir Relatório
        </button>
        <h1 class="card-title">Movimentação financeira</h1>
      </div>
      <div class="card-body">
        <div class="cr-toolbar ft-toolbar">
          <div class="cr-toolbar-field form-group">
            <label class="form-label">Data</label>
            <select id="filter-date" class="form-select" v-model="filter.dateType">
              <option value="issueDate">Emissão</option>
              <option value="dueDate">Vencimento</option>
              <option value="paymentDate">Baixa</option>
            </select>
          </div>

          <div class="cr-toolbar-field ft-field-period form-group">
            <label class="form-label">Período</label>
            <st-date-period
              :start-date.sync="filter.startDate"
              :end-date.sync="filter.endDate"
              :start-date-validator="$v.filter.startDate"
              :end-date-validator="$v.filter.endDate"
              @blur:startDate="$v.filter.startDate.$touch()"
              @blur:endDate="$v.filter.endDate.$touch()"
            />
          </div>

          <div class="cr-toolbar-field form-group">
            <label class="form-label">Tipo</label>
            <select id="filter-type" name="filter-type"
                    class="form-select" v-model="filter.type">
              <option value="">Todos</option>
              <option value="expense">Documentos a pagar</option>
              <option value="revenue">Documentos a receber</option>
            </select>
          </div>

          <div class="cr-toolbar-field form-group">
            <label class="form-label">Status</label>
            <select id="filter-status" name="filter-status"
                    class="form-select" v-model="filter.status">
              <option value="">Todos</option>
              <option value="canceled">Cancelados</option>
              <option value="paid">Baixados</option>
              <option value="open">Pendentes</option>
            </select>
          </div>

          <div class="cr-toolbar-field form-group" v-if="moreFilters">
            <label class="form-label">Conteúdo</label>
            <select id="filter-only-debt" name="filter-only-debt"
                    class="form-select" v-model="filter.onlyDebt">
              <option value="">Tudo</option>
              <option value="true">Sem crédito e transferência</option>
              <option value="cashRegister">Apenas recebimentos do caixa</option>
              <option value="billingTransfer"
                      v-if="insuranceBillingEnabled">Apenas repasses (novo)</option>
              <option value="transfer">
                {{ insuranceBillingEnabled ? 'Apenas repasses (legado)' : 'Apenas repasses' }}
              </option>
              <option value="batch">Apenas títulos de lote</option>
            </select>
          </div>

          <div class="cr-toolbar-field form-group" v-if="moreFilters">
            <label class="form-label">Forma de pagamento</label>
            <select id="filter-payment" name="filter-payment"
                    class="form-select" v-model="filter.methodId">
              <option value="">Todas</option>
              <option v-for="(item, i) in paymentMethods" :value="item.id" :key="i">
                {{ item.name }}
              </option>
            </select>
          </div>

          <div class="cr-toolbar-field form-group" v-if="moreFilters">
            <label class="form-label">Conta financeira</label>
            <select id="filter-bank-account" class="form-select" v-model="filter.bankAccountId">
              <option value="">Todas</option>
              <option v-for="(item, i) in bankAccounts" :value="item.id" :key="i">
                {{ item.name }}
              </option>
            </select>
          </div>

          <div class="cr-toolbar-field form-group" v-if="moreFilters">
            <label for="filter-document" class="form-label">Documento</label>
            <input type="text" id="filter-document" name="filter-document"
                   class="form-input" v-model="filter.document" maxlength="20"
                   autocomplete="nope" placeholder="XT7A9SAQ" @keypress.enter="load">
          </div>

          <div class="cr-toolbar-field ft-field-search form-group" v-if="moreFilters">
            <label for="filter-search" class="form-label">Pagador / Favorecido</label>
            <input type="text" id="filter-search" name="filter-search"
                   class="form-input" v-model="filter.search" maxlength="100"
                   autocomplete="nope" placeholder="José da Silva..." @keypress.enter="load">
          </div>

          <div class="cr-toolbar-actions ft-field-actions">
            <button class="btn btn-link btn-sm ft-more" @click="moreFilters = !moreFilters">
              {{ moreFilters ? 'Menos filtros' : 'Mais filtros' }}
              <fa-icon :icon="['fal', moreFilters ? 'chevron-up' : 'chevron-down']"/>
            </button>
            <button class="btn btn-link btn-sm ft-clear" @click="clearFilters">
              Limpar filtros
            </button>
            <button class="btn btn-secondary btn-icon btn-icon-left"
                    :class="{loading}" :disabled="loading" @click="load">
              <fa-icon :icon="['fal', 'search']"/>Pesquisar
            </button>
          </div>
        </div>

        <div class="loading mt-2" v-if="loading"></div>
        <template v-else>
          <template v-if="data.items.length === 0">
            <div class="empty mt-2">
              <div class="empty-icon">
                <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
              </div>
              <p class="empty-title h5">Movimentação financeira</p>
              <p class="empty-subtitle">
                Nenhuma movimentação encontrado. Verifique os filtros para realizar novas buscas
              </p>
            </div>
          </template>
          <template v-else>
            <table class="table table-striped table-hover">
              <thead>
              <tr>
                <th class="text-center" width="40px">Tipo</th>
                <th class="text-center">Status</th>
                <th>Documento</th>
                <th class="text-center">Emissão</th>
                <th class="text-center">Vencimento</th>
                <th class="text-center">Baixa</th>
                <th>Pagador / Favorecido</th>
                <th>Cobrança</th>
                <th class="text-right">Valor</th>
                <th style="width: 4.6rem;"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, i) in data.items" :key="i">
                <td class="text-center">
                  <div class="tooltip tooltip-right" :data-tooltip="'Pagar'"
                       v-if="item.type === 'expense'">
                    <fa-icon class="text-warning" :icon="['fal', 'arrow-up']"/>
                  </div>
                  <div class="tooltip tooltip-right" :data-tooltip="'Receber'" v-else>
                    <fa-icon class="text-secondary" :icon="['fal', 'arrow-down']"/>
                  </div>
                </td>
                <td class="text-center">
                  <div class="tooltip tooltip-right" v-if="item.status === 'open'"
                       :data-tooltip="'Pendente'">
                    <fa-icon class="text-gray" :icon="['fal', 'minus']"/>
                  </div>
                  <div class="tooltip tooltip-right" v-if="item.status === 'paid'"
                       :data-tooltip="'Realizado'">
                    <fa-icon class="text-info" :icon="['fal', 'check']"/>
                  </div>
                  <div class="tooltip tooltip-right" v-if="item.status === 'canceled'"
                       :data-tooltip="'Cancelado'">
                    <fa-icon class="text-error" :icon="['fal', 'times']"/>
                  </div>
                </td>
                <td>
                  <span class="tooltip hover-link" data-tooltip="Detalhar documento"
                        @click="edit(item)">
                    {{ item.document }}
                  </span>
                </td>
                <td class="text-center">
                  {{ item.issueDate | date }}
                </td>
                <td class="text-center">
                  {{ item.dueDate | date }}
                </td>
                <td class="text-center">
                  <span v-if="item.payments && item.payments.length > 0">
                    {{ item.payments[0].date | date }}
                  </span>
                  <span v-else>-</span>
                </td>
                <td>
                  <span class="tooltip text-success mr-1"
                        v-if="isReconciled(item)"
                        data-tooltip="Conciliado com o extrato bancário">
                    <fa-icon :icon="['fal', 'lock']" />
                  </span>{{ item.entity ? item.entity.name : '—' }}
                </td>
                <td>
                  <span>{{ item.method ? item.method.name : '—' }}</span>
                  <span v-if="item.installment && item.installment.total > 1">
                    ({{ item.installment.current }}/{{ item.installment.total }})
                  </span>
                </td>
                <td class="text-right" style="min-width: 100px">
                  <div class="text-primary" v-if="item.type === 'revenue'">
                    <span v-if="item.payments && item.payments.length > 0">
                      {{ item.payments[0].total | currency }}
                    </span>
                    <span v-else>{{ item.value.final | currency }}</span>
                  </div>
                  <div class="text-error" v-else>
                    <span v-if="item.payments && item.payments.length > 0">
                      {{ item.payments[0].total * -1 | currency }}
                    </span>
                    <span v-else>{{ (item.value.final * -1) | currency }}</span>
                  </div>
                </td>
                <td class="text-right">
                  <button
                    v-if="item.type === 'revenue'
                      && item.entity.type !== 'insurance'
                      && item.status === 'paid'"
                    class="btn btn-sm btn-gray btn-icon btn-action mr-1"
                    :class="[invoiceModal.loading ? 'loading' : 'tooltip']"
                    data-tooltip="Nota fiscal"
                    @click="openInvoiceModal(item)"
                  ><fa-icon :icon="['fal', 'file-invoice-dollar']"/></button>

                  <template v-if="item.status === 'open'">
                    <button class="btn tooltip btn-sm btn-action btn-icon btn-secondary mr-1"
                            @click="edit(item)"
                            :disabled="isReconciled(item)"
                            :data-tooltip="isReconciled(item) ? reconciledTooltip : 'Alterar'">
                      <fa-icon :icon="['fal', 'pencil']"></fa-icon>
                    </button>
                    <button class="btn tooltip btn-sm btn-action btn-icon btn-warning"
                            @click="payment(item.id)"
                            :disabled="isReconciled(item)"
                            :data-tooltip="isReconciled(item) ? reconciledTooltip : 'Baixar'">
                      <fa-icon :icon="['fal', 'check-circle']"></fa-icon>
                    </button>
                  </template>
                  <template v-else>
                    <button class="btn tooltip btn-sm btn-action btn-icon btn-gray mr-1"
                            data-tooltip="Imprimir" disabled>
                      <fa-icon :icon="['fal', 'print']"></fa-icon>
                    </button>
                    <button class="btn tooltip btn-sm btn-action btn-icon btn-warning"
                            @click="reverse(item)"
                            :disabled="isReconciled(item)"
                            :data-tooltip="isReconciled(item) ? reconciledTooltip : 'Estornar'">
                      <fa-icon :icon="['fal', 'undo']"></fa-icon>
                    </button>
                  </template>
                </td>
              </tr>
              </tbody>
            </table>
          </template>
        </template>
      </div>
      <div class="card-footer text-right" v-if="data.items.length > 0">
        Total:<span class="ml-2 text-bold">
        {{ total | currency }}
      </span>
      </div>
    </div>

    <detail-modal
      id="modal-detail"
      v-if="detail.show"
      :show="detail.show"
      :transactionId="detail.id"
      @close="closeDetail"
    />

    <invoice-detail-or-create
      v-if="invoiceModal.show"
      :show="invoiceModal.show"
      :total-amount="invoiceModal.value"
      :entity="invoiceModal.borrower"
      origin-type="financial_transaction"
      :origin-id="invoiceModal.refId"
      @loading="invoiceModal.loading = $event"
      @close="closeInvoiceModal"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import moment from 'moment';
import { date, minDate } from '@/data/validators';
import localforage from 'localforage';
import InvoiceDetailOrCreate from '@/components/fiscal-document/FiscalDocument.vue';
import { blocksOriginTransaction } from '@/components/fiscal-document/statuses';
import {
  PRINT_FINANCIAL_TRANSACTION_LIST,
  REVERSE_FINANCIAL_TRANSACTION,
  PAY_FINANCIAL_TRANSACTION,
} from '@/data/actions/modules/financial';
import { isEnabled as isInsuranceBillingEnabled } from '@/app/billing/guide/service';
import DetailModal from './Detail.vue';
import { parse as parseBoolean } from '../../../../helpers/boolean';

export default {
  components: {
    InvoiceDetailOrCreate,
    DetailModal,
  },
  data() {
    return {
      path: '/financial-transactions',
      loading: false,
      printing: false,
      detail: {
        id: '',
        show: false,
      },
      paymentMethods: [],
      bankAccounts: [],
      moreFilters: false,
      filter: {
        type: '',
        status: 'open',
        dateType: 'dueDate',
        startDate: moment()
          .format('YYYY-MM-DD'),
        endDate: moment()
          .add(15, 'days')
          .format('YYYY-MM-DD'),
        methodId: '',
        bankAccountId: '',
        transfer: '',
        onlyDebt: '',
        document: '',
        search: '',
      },
      data: {
        items: [],
        total: 0,
        limit: 30,
        offset: 0,
      },
      invoiceModal: {
        loading: false,
        show: false,
        id: null,
        refId: null,
        value: 0,
        borrower: null,
      },
    };
  },
  mounted() {
    this.loadPaymentMethods();
    this.loadBankAccounts();
    this.loadFilters()
      .then(() => {
        this.applyQuery();
        this.load();
      });
  },
  computed: {
    ...mapState({
      user: ({ auth }) => auth.user,
      branch: ({ auth }) => auth.user.branch,
    }),
    insuranceBillingEnabled() {
      return isInsuranceBillingEnabled(this.user);
    },
    isFiscalDocumentEnabled() {
      const settings = this.branch
        && this.branch.settings
        && this.branch.settings.fiscalDocument;
      return !!(settings && settings.enabled);
    },
    total() {
      return this.data.items
        .reduce((a, b) => {
          let value;
          if (b.payments && b.payments.length > 0) {
            value = b.payments[0].total;
          } else {
            value = b.value.final;
          }
          a += b.type === 'revenue' ? value : value * -1;
          return a;
        }, 0);
    },
    reconciledTooltip() {
      return 'Conciliado com o extrato bancário. Desfaça a conciliação no Extrato para alterar.';
    },
    canAccessPrintTransaction() {
      if (PRINT_FINANCIAL_TRANSACTION_LIST) {
        return this.$can(PRINT_FINANCIAL_TRANSACTION_LIST);
      }
      return true;
    },
    canAccessReverseTransaction() {
      if (REVERSE_FINANCIAL_TRANSACTION) {
        return this.$can(REVERSE_FINANCIAL_TRANSACTION);
      }
      return true;
    },
    canAccessPayTransaction() {
      if (PAY_FINANCIAL_TRANSACTION) {
        return this.$can(PAY_FINANCIAL_TRANSACTION);
      }
      return true;
    },
  },
  validations() {
    const rules = {
      filter: {
        startDate: {
          date,
          required,
        },
        endDate: {
          date,
          required,
        },
      },
    };

    if (this.filter.startDate && moment(this.filter.startDate).isValid()) {
      rules.filter.endDate = {
        date,
        minDate: minDate(this.filter.startDate),
      };
    }

    return rules;
  },
  methods: {
    clearFilters() {
      this.filter = {
        ...this.filter,
        type: '',
        status: 'open',
        onlyDebt: '',
        methodId: '',
        bankAccountId: '',
        document: '',
        search: '',
      };
      this.load();
    },
    applyQuery() {
      const query = this.$route.query || {};
      const keys = ['type', 'status', 'dateType', 'startDate', 'endDate', 'onlyDebt'];
      keys.forEach((key) => {
        if (query[key]) this.filter[key] = String(query[key]);
      });
      const applied = keys.some(key => !!query[key]);
      if (query.onlyDebt) this.moreFilters = true;
      return applied;
    },
    isReconciled(item) {
      return !!item.reconciliation && item.reconciliation.status === 'done';
    },
    loadFilters() {
      return localforage.getItem('financialFilters')
        .then((filters) => {
          if (filters) {
            if (filters.dateType) {
              this.filter.dateType = filters.dateType;
            }

            if (filters.startDate) {
              this.filter.startDate = filters.startDate;
            }

            if (filters.endDate) {
              this.filter.endDate = filters.endDate;
            }

            if (filters.type) {
              this.filter.type = filters.type;
            }

            if (parseBoolean(filters.transfer)) {
              this.filter.transfer = filters.transfer;
            }

            if (filters.status) {
              this.filter.status = filters.status;
            }

            if (filters.bankAccountId) {
              this.filter.bankAccountId = filters.bankAccountId;
            }

            if (filters.methodId) {
              this.filter.methodId = filters.methodId;
            }

            if (filters.document) {
              this.filter.document = filters.document;
            }

            if (filters.search) {
              this.filter.search = filters.search;
            }

            const duration = moment.duration(moment(this.filter.endDate)
              .diff(moment(this.filter.startDate))).asMonths();

            if (duration >= 7) {
              this.filter.startDate = moment().format('YYYY-MM-DD');
              this.filter.endDate = moment().add(1, 'months')
                .format('YYYY-MM-DD');
            }
          }
        })
        .catch(() => {});
    },
    loadPaymentMethods() {
      const params = {
        active: true,
        limit: 0,
      };
      return this.$http.get('/payment-methods', { params })
        .then(({ data }) => {
          this.paymentMethods = data.items;
        })
        .catch(() => {
        });
    },
    loadBankAccounts() {
      const params = {
        active: true,
        limit: 0,
      };
      return this.$http.get('/bank-accounts', { params })
        .then(({ data }) => {
          this.bankAccounts = data.items;
        })
        .catch(() => {
        });
    },
    load() {
      this.$v.filter.$touch();
      if (this.$v.filter.$error) {
        return;
      }

      const duration = moment.duration(moment(this.filter.endDate)
        .diff(moment(this.filter.startDate))).asMonths();

      if (duration >= 7) {
        this.$toast.show('Período máximo (6 meses)', { type: 'error' });
        return;
      }

      localforage.setItem('financialFilters', this.filter);

      this.loading = true;

      const params = {};

      if (this.filter.dateType) {
        params.dateType = this.filter.dateType;
      }

      if (this.filter.startDate) {
        params.startDate = this.filter.startDate;
      }

      if (this.filter.endDate) {
        params.endDate = this.filter.endDate;
      }

      if (this.filter.type) {
        params.type = this.filter.type;
      }

      if (this.filter.onlyDebt === 'transfer') {
        params.transfer = 'true';
      }

      if (this.filter.onlyDebt === 'billingTransfer') {
        params.billingOperation = 'transfer';
      }

      if (this.filter.onlyDebt === 'batch') {
        params.billingOperation = 'receivable';
      }

      if (this.filter.onlyDebt === 'cashRegister') {
        params.cashRegister = 'true';
        params.onlyDebt = 'true';
      }

      if (parseBoolean(this.filter.onlyDebt)) {
        params.onlyDebt = 'true';
      }

      if (this.filter.status) {
        params.status = this.filter.status;
      }

      if (this.filter.bankAccountId) {
        params.bankAccountId = this.filter.bankAccountId;
      }

      if (this.filter.methodId) {
        params.methodId = this.filter.methodId;
      }

      if (this.filter.document) {
        params.document = this.filter.document;
      }

      if (this.filter.search) {
        params.search = this.filter.search;
      }

      this.$http.get(this.path, { params })
        .then(({ data }) => {
          this.data = data;
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    open() {
      this.$router.push('/financial/transactions/create');
    },
    edit(item) {
      if (!item.id) {
        return;
      }
      if (this.blockedByFiscalDocument(item, 'editar')) {
        return;
      }
      if (item.status === 'paid' || (item.services && item.services.length > 0)) {
        this.detail.id = item.id;
        this.detail.show = true;
      } else {
        this.$router.push(`/financial/transactions/${item.id}/edit`);
      }
    },
    blockedByFiscalDocument(item, action) {
      // Só valida quando o recurso de nota fiscal está ativo.
      if (!this.isFiscalDocumentEnabled) {
        return false;
      }
      const blocked = blocksOriginTransaction(item.fiscalDocument);
      if (blocked) {
        this.$dialog.show('Ação não permitida', {
          type: 'danger',
          html: 'Esta transação possui uma nota fiscal ativa. '
            + `Cancele a nota fiscal antes de ${action} o documento.`,
        });
      }
      return blocked;
    },
    print() {
      if (!this.canAccessPrintTransaction) {
        this.$toast.show('Acesso não autorizado!', { type: 'error' });
        return false;
      }
      this.printing = true;
      const ids = this.data.items.map(({ id }) => id);
      const params = {
        ids: ids.join(','),
        status: this.filter.status,
        dateType: this.filter.dateType,
        startDate: this.filter.startDate,
        endDate: this.filter.endDate,
      };
      return this.$file
        .print(`${this.path}/print`, params)
        .catch(() => {})
        .finally(() => {
          this.printing = false;
        });
    },
    closeDetail(save) {
      this.detail.show = false;
      if (save) {
        this.load();
      }
    },
    payment(id) {
      if (!this.canAccessPayTransaction) {
        this.$toast.show('Acesso não autorizado!', { type: 'error' });
        return;
      }
      this.$router.push(`/financial/transactions/${id}/payment`);
    },
    reverse(item) {
      if (!this.canAccessReverseTransaction) {
        this.$toast.show('Acesso não autorizado!', { type: 'error' });
        return;
      }
      if (this.blockedByFiscalDocument(item, 'estornar')) {
        return;
      }
      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Deseja realmente estornar este documento?</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: close => this.$http
              .put(`${this.path}/${item.id}/reverse`)
              .catch((e) => {
                if (e.response
                  && e.response.data
                  && e.response.data.message) {
                  this.$toast.show(e.response.data.message, { type: 'error', timeout: 5000 });
                }
              })
              .finally(() => {
                close();
                this.load();
              }),
          },
        ],
      });
    },
    async openInvoiceModal(item) {
      this.invoiceModal.loading = true;
      try {
        this.invoiceModal.value = item.value.total;
        this.invoiceModal.borrower = item.entity;
        this.invoiceModal.refId = item.id;
        this.invoiceModal.loading = false;
        this.invoiceModal.show = true;
      } catch (e) {
        this.$toast.error(e);
        this.invoiceModal.loading = false;
      }
    },
    closeInvoiceModal() {
      this.invoiceModal.value = null;
      this.invoiceModal.borrower = null;
      this.invoiceModal.refId = null;
      this.invoiceModal.show = false;
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";

.financial-transactions-page {
  .table th, .table td {
    font-size: 0.56rem;
  }
  .cr-toolbar {
    align-items: flex-end;
    background-color: $gray-color-ultra-light;
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing;
    margin-bottom: $layout-spacing-lg;
    padding: $layout-spacing;
    .cr-toolbar-field {
      flex: 1 1 8.5rem;
      min-width: 0;
    }
    .form-group {
      margin-bottom: 0;
    }
    .form-label {
      color: $gray-color-dark;
      font-size: $font-size-xs;
    }
    .ft-field-period {
      flex: 1 1 15rem;
    }
    .ft-field-search {
      flex: 1 1 12rem;
    }
    .cr-toolbar-actions {
      align-items: center;
      display: flex;
      flex: 0 0 auto;
      gap: $layout-spacing;
      margin-left: auto;
      .ft-more svg {
        margin-left: .2rem;
      }
    }
  }
  @media screen and (max-width: $size-sm) {
    .cr-toolbar .cr-toolbar-actions {
      flex: 1 1 100%;
      justify-content: flex-end;
    }
  }
}

#transaction-tiles {
  display: flex;
  padding: 0 0 $layout-spacing 0;
  color: #fff;

  .tile-item {
    background-color: $secondary-color;
    border-radius: $border-radius;
    flex: 1;
    margin-left: $layout-spacing;
    padding: 10px;
    position: relative;
    text-align: center;

    &:first-child {
      margin-left: 0;
    }
  }

  .tile-label {
    font-size: $font-size-sm;
    line-height: $font-size-sm;
    margin: $layout-spacing-sm 0 calc($layout-spacing-sm / 2) 0;
  }

  .tile-value {
    font-size: 14px;
    font-weight: 600;
    line-height: $font-size;
    margin-top: $layout-spacing;
  }
}
</style>
